import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@blueprintjs/core'
import { MinimalStateDot } from '../badge/MinimalStateDot'
import { MatchBadge, LaneBadge, LANE_TITLE } from './prototypes/sharedRoutingHelpers'
import type { BadgeType } from './prototypes/sharedRoutingHelpers'
import './prototypes/routing-cards.css'
import { SetItems } from '../../types/promotions'
import { AddHeaderButton } from './RuleEditorPanels'
import PromotionItemI = Models.PromotionItemI
import PromotionRuleI = Models.PromotionRuleI

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Map numeric ad_group_type (1=keyword, 2=target, 3=auto) to MatchBadge type. */
const numericTypeToBadge = (type: number): BadgeType | null => {
    if (type === 3) return 'auto'
    if (type === 2) return 'product'
    if (type === 1) return 'broad'
    return null
}

interface UsageCounts {
    sourceRules: number
    promoteRules: number
    negateRules: number
}

// ── Sub-components ────────────────────────────────────────────────────────────

const UsageCell = ({ count, lane }: { count: number; lane: 'src' | 'tgt' | 'neg' }) =>
    count > 0 ? (
        <LaneBadge type={lane}>{count}</LaneBadge>
    ) : (
        <span className="text-[12px] text-[#C8D2DC]">—</span>
    )

const ChevronIcon = ({ collapsed }: { collapsed: boolean }) => (
    <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="none"
        className="text-[#94A3B8] flex-shrink-0 transition-transform duration-150"
        style={{ transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
    >
        <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const RemoveItemButton = ({ onClick }: { onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        className="size-6 inline-flex items-center justify-end rounded-md text-[#CDD6DF] hover:text-[#FF5C5C] hover:bg-[#FFF0F0] transition shrink-0 opacity-0 group-hover:opacity-100"
        aria-label="Remove"
    >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path
                d="M3 3l6 6M9 3l-6 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    </button>
)

// ── Main component ────────────────────────────────────────────────────────────

interface PromotionLinkedAdGroupsNewProps {
    items: PromotionItemI[]
    rules: PromotionRuleI[]
    setItems: (items: SetItems) => any
    setShowAdd: (v: boolean) => void
    screenshotMode: boolean
    disableAddItems?: boolean
    disableRemoveItems?: boolean
    removeItem: (item: PromotionItemI) => void
}

export const PromotionLinkedAdGroupsNew = ({
    items,
    rules,
    setShowAdd,
    screenshotMode,
    disableAddItems,
    disableRemoveItems,
    removeItem,
}: PromotionLinkedAdGroupsNewProps) => {
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set())

    const toggleCampaign = (campaignId: number) =>
        setCollapsed((prev) => {
            const next = new Set(prev)
            next.has(campaignId) ? next.delete(campaignId) : next.add(campaignId)
            return next
        })

    // Group items by campaign
    const byCampaign = useMemo(() => {
        const map = new Map<
            number,
            {
                campaignId: number
                campaignName: string
                campaignState: any
                items: PromotionItemI[]
            }
        >()
        for (const item of items) {
            if (!map.has(item.campaign_id)) {
                map.set(item.campaign_id, {
                    campaignId: item.campaign_id,
                    campaignName: item.campaign_name,
                    campaignState: item.campaign_state,
                    items: [],
                })
            }
            map.get(item.campaign_id)!.items.push(item)
        }
        return [...map.values()]
    }, [items])

    // Compute rule usage counts per (campaign_id, ad_group_id)
    const usageMap = useMemo(() => {
        const map = new Map<string, UsageCounts>()
        for (const item of items) {
            map.set(`${item.campaign_id}:${item.ad_group_id}`, {
                sourceRules: 0,
                promoteRules: 0,
                negateRules: 0,
            })
        }
        for (const rule of rules ?? []) {
            for (const ri of rule.rule_items ?? []) {
                const key = `${ri.campaign_id}:${ri.ad_group_id}`
                const counts = map.get(key)
                if (!counts) continue
                if (ri.use_search_terms) counts.sourceRules++
                if (
                    ri.promotion_broad ||
                    ri.promotion_phrase ||
                    ri.promotion_exact ||
                    ri.promotion_products
                )
                    counts.promoteRules++
                if (ri.negation_phrase || ri.negation_exact || ri.negation_products)
                    counts.negateRules++
            }
        }
        return map
    }, [items, rules])

    return (
        <div className="routing-cards-root">
            {/* Card header */}
            <div className="pt-2.5 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[15px] font-[600] text-[#0E2947]">
                    Linked Ad Groups
                    <span className="text-[12px] font-[400] text-[#94A3B8]">{items.length}</span>
                </div>
                {!disableAddItems && (
                    <AddHeaderButton label="Link Ad Group" onClick={() => setShowAdd(true)} />
                )}
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: '480px' }}>
                {/* Column header */}
                <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-1.5 bg-[#FAFBFC]">
                    <div className="flex-1 text-[12px] font-[500] uppercase tracking-[0.04em] text-[#94A3B8]">
                        Ad Group
                    </div>
                    {/* type badge column — no header label, just aligns with rows */}
                    <div className="w-20 flex-shrink-0" />
                    <div className="w-10 flex justify-end flex-shrink-0">
                        <LaneBadge type="src" />
                    </div>
                    <div className="w-14 flex justify-end flex-shrink-0">
                        <LaneBadge type="tgt" />
                    </div>
                    <div className="w-12 flex justify-end flex-shrink-0">
                        <LaneBadge type="neg" />
                    </div>
                    {/* spacer matching the remove button width */}
                    <div className="w-6 flex-shrink-0" />
                </div>

                {/* Grouped rows */}
                {byCampaign.map(({ campaignId, campaignName, campaignState, items: groupRows }) => {
                    const isCollapsed = collapsed.has(campaignId)
                    return (
                        <React.Fragment key={campaignId}>
                            {/* Campaign header row */}
                            <div
                                className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] border-t border-[#EDF0F3] cursor-pointer hover:bg-[#F1F3F6] transition-colors select-none"
                                onClick={() => toggleCampaign(campaignId)}
                            >
                                <ChevronIcon collapsed={isCollapsed} />
                                <MinimalStateDot state={campaignState} />
                                <Link
                                    to={`/campaigns/${campaignId}`}
                                    className={
                                        'text-[13px] font-[500] text-[#0E2947] hover:text-[#1A6FD4] truncate transition-colors' +
                                        (screenshotMode ? ' blur' : '')
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {campaignName}
                                </Link>
                                <span className="text-[12px] text-[#94A3B8] flex-shrink-0">
                                    {groupRows.length}
                                </span>
                            </div>

                            {/* Ad group rows */}
                            {!isCollapsed &&
                                groupRows.map((item) => {
                                    const key = `${item.campaign_id}:${item.ad_group_id}`
                                    const usage = usageMap.get(key) ?? {
                                        sourceRules: 0,
                                        promoteRules: 0,
                                        negateRules: 0,
                                    }
                                    const badge = numericTypeToBadge(item.ad_group_type)

                                    return (
                                        <div
                                            key={item.ad_group_id}
                                            className="group flex items-center gap-2 pl-10 pr-4 py-2 border-t border-[#EDF0F3] hover:bg-[#FAFBFC] transition-colors"
                                        >
                                            <MinimalStateDot state={item.ad_group_state} />
                                            {/* Name */}
                                            <Link
                                                to={`/ad-groups/${item.ad_group_id}`}
                                                className={
                                                    'flex-1 text-[14px] text-[#0E2947] hover:text-[#1A6FD4] truncate transition-colors min-w-0' +
                                                    (screenshotMode ? ' blur' : '')
                                                }
                                            >
                                                {item.ad_group_name}
                                            </Link>
                                            {/* Type badge — own fixed column so usage columns stay aligned */}
                                            <div className="w-20 flex-shrink-0 flex justify-end">
                                                {badge && <MatchBadge type={badge} />}
                                            </div>
                                            {/* Usage columns */}
                                            <div className="w-10 flex justify-end flex-shrink-0">
                                                <UsageCell count={usage.sourceRules} lane="src" />
                                            </div>
                                            <div className="w-14 flex justify-end flex-shrink-0">
                                                <UsageCell count={usage.promoteRules} lane="tgt" />
                                            </div>
                                            <div className="w-12 flex justify-end flex-shrink-0">
                                                <UsageCell count={usage.negateRules} lane="neg" />
                                            </div>
                                            {!disableRemoveItems ? (
                                                <RemoveItemButton
                                                    onClick={() => removeItem(item)}
                                                />
                                            ) : (
                                                <div className="w-6 flex-shrink-0" />
                                            )}
                                        </div>
                                    )
                                })}
                        </React.Fragment>
                    )
                })}
            </div>
        </div>
    )
}
